import { type ClosedFenceSpan, scanFences } from "../markdown/trailingOpenFence"
import { mermaidStreamPreview } from "../markdown/mermaidStreamPreview"
import { withoutUnclosedTrailingLink } from "./withoutUnclosedTrailingLink"

const MERMAID_LANGUAGE = "mermaid"
const INLINE_CODE_PATTERN = /(`+)[^`][\s\S]*?\1/g
const DISPLAY_MATH_OPENER_PATTERN = /(^|\s)\$\$(?=\S)|^\$\$$/gm
const DISPLAY_MATH_DELIMITER = "$$"
const COMMENT_OPENER = "<!--"
const COMMENT_CLOSER = "-->"
const RAW_TEXT_OPENER_PATTERN = /<(style|script)(?:\s[^<>]*)?>/gi
const RAW_TEXT_CLOSER_PATTERN = /<\/(style|script)\s*>/gi

function blanked(text: string): string {
  return " ".repeat(text.length)
}

function withSpansBlanked(content: string, spans: ClosedFenceSpan[]): string {
  return spans.reduce(
    (blankedContent, span) => blankedContent.slice(0, span.start) + blanked(content.slice(span.start, span.end)) + blankedContent.slice(span.end),
    content,
  )
}

function withCodeBlanked(content: string, closedFences: ClosedFenceSpan[]): string {
  return withSpansBlanked(content, closedFences).replace(INLINE_CODE_PATTERN, blanked)
}

function lastMatchIndex(pattern: RegExp, text: string): number {
  let last = -1
  for (const match of text.matchAll(pattern)) last = match.index ?? -1
  return last
}

function unclosedDisplayMathStart(prose: string): number {
  const delimiterCount = prose.split(DISPLAY_MATH_DELIMITER).length - 1
  if (delimiterCount % 2 === 0) return -1
  const lastDelimiter = prose.lastIndexOf(DISPLAY_MATH_DELIMITER)
  const openers = Array.from(prose.matchAll(DISPLAY_MATH_OPENER_PATTERN), (match) => (match.index ?? 0) + match[1].length)
  return openers.includes(lastDelimiter) ? lastDelimiter : -1
}

function unclosedCommentStart(prose: string): number {
  const opener = prose.lastIndexOf(COMMENT_OPENER)
  return opener > prose.lastIndexOf(COMMENT_CLOSER) ? opener : -1
}

function unclosedRawTextElementStart(prose: string): number {
  const opener = lastMatchIndex(RAW_TEXT_OPENER_PATTERN, prose)
  return opener > lastMatchIndex(RAW_TEXT_CLOSER_PATTERN, prose) ? opener : -1
}

function withoutUnclosedTrailingHold(content: string, closedFences: ClosedFenceSpan[]): string {
  const prose = withCodeBlanked(content, closedFences)
  const holdStarts = [unclosedDisplayMathStart(prose), unclosedCommentStart(prose), unclosedRawTextElementStart(prose)].filter(
    (start) => start >= 0,
  )
  if (holdStarts.length === 0) return content
  return content.slice(0, Math.min(...holdStarts))
}

export function streamSafeMarkdown(content: string): string {
  const { closedSpans, trailingOpen } = scanFences(content)
  if (!trailingOpen) return withoutUnclosedTrailingLink(withoutUnclosedTrailingHold(content, closedSpans))
  if (trailingOpen.bodyStart === null) return content.slice(0, trailingOpen.openerStart)
  if (trailingOpen.language !== MERMAID_LANGUAGE) return content
  return content.slice(0, trailingOpen.bodyStart) + mermaidStreamPreview(content.slice(trailingOpen.bodyStart))
}
